import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { PAYFAC_QUERIES } from "./queries";
import { bffApi, sellerRatesApi, BFF_BASE_URL, N8N_BASE_URL, N8N_BARTE_TAXES_URL } from "./api-rates";

import type { ISellerRates, ISellerRatesConfig, IBarteTaxes } from "@payfac/types";


const arrayToRateRecord = (rates: number[]): Record<string, number> =>
  rates.reduce((acc, rate, i) => ({ ...acc, [`${i + 2}x`]: rate }), {});

const buildUpdatePayload = (sellerId: string, config: ISellerRatesConfig, observation: string, receiptType?: string) => {
  const { earlyCreditCardConfigData, ...rest } = config;

  const earlyCredit = earlyCreditCardConfigData?.sellerRates?.length
    ? {
        earlyCreditCardConfigData: {
          ...earlyCreditCardConfigData,
          sellerRates: arrayToRateRecord(earlyCreditCardConfigData.sellerRates),
          buyerRates: arrayToRateRecord(earlyCreditCardConfigData.buyerRates ?? []),
        },
      }
    : {};

  const isTimeline = receiptType === "TIMELINE";

  return {
    idSeller: sellerId,
    observation,
    receiptType,
    currentSellerRatesRequest: {
      ...rest,
      sightCreditCardConfigData: {
        ...rest.sightCreditCardConfigData,
        delayForRedeem: isTimeline ? 30 : rest.sightCreditCardConfigData?.delayForRedeem,
      },
      ...earlyCredit,
    },
  };
};


export const useGetBarteTaxes = () =>
  useQuery({
    queryKey: [PAYFAC_QUERIES.BARTE_TAXES],
    queryFn: async (): Promise<IBarteTaxes> => {
      const { data } = await sellerRatesApi.get(N8N_BARTE_TAXES_URL);
      return data;
    },
  });

export const useGetSellerRates = ({ sellerId }: { sellerId: string }) => {
  const { t } = useTranslation();

  const queryFn = async (): Promise<{
    success: boolean;
    message: string;
    data?: ISellerRates;
  }> => {
    try {
      const { data } = await bffApi.get(`${BFF_BASE_URL}/sellers/${sellerId}/rates`);

      return {
        success: true,
        message: t("payfac.pageRatesGetSuccess"),
        data: data as ISellerRates,
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error?.response?.data?.message ||
          error?.response?.message ||
          t("payfac.pageRatesGetError"),
      };
    }
  };

  return useQuery({
    queryKey: [PAYFAC_QUERIES.SELLER_RATES, sellerId],
    queryFn,
    enabled: !!sellerId,
  });
};

export const useUpdateSellerRates = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const updateSellerRates = async ({
    sellerId,
    payload,
    receiptType,
    rawPayload,
  }: {
    sellerId: string;
    payload?: ISellerRatesConfig;
    receiptType?: string;
    rawPayload?: Record<string, any>;
  }): Promise<{ success: boolean; message: string; data?: any }> => {
    try {
      const body = rawPayload ?? buildUpdatePayload(sellerId, payload!, t("payfac.pageRatesUpdateObservation"), receiptType);
      const { data } = await sellerRatesApi.patch(
        `${N8N_BASE_URL}?idSeller=${sellerId}`,
        body,
      );

      return {
        success: true,
        message: t("payfac.pageRatesUpdateSuccess"),
        data,
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error?.response?.data?.message ||
          error?.response?.message ||
          t("payfac.pageRatesUpdateError"),
        data: error?.response?.data,
      };
    }
  };

  return useMutation({
    mutationFn: updateSellerRates,
    onSuccess: (response, variables) => {
      if (response.success) {
        queryClient.invalidateQueries({
          queryKey: [PAYFAC_QUERIES.SELLER_RATES, variables.sellerId],
        });
      }
    },
  });
};
